'use client';

import { ComponentProps, createContext, useContext, useId, useMemo } from 'react';
import * as RechartsPrimitive from 'recharts';

import { cn } from '../../../lib';

import { THEMES, type IChartConfig } from './types';

type ChartContextProps = {
  config: IChartConfig;
};

const ChartContext = createContext<ChartContextProps | null>(null);

/**
 * -----------------------------------------------------------------------------
 * Custom hook to access the chart context.
 */
export function useChart() {
  const context = useContext(ChartContext);

  if (!context) {
    throw new Error('useChart must be used within a <ChartContainer />');
  }

  return context;
}

/**
 * -----------------------------------------------------------------------------
 * Custom component to apply the chart styles.
 */
export function ChartStyle({ id, config }: { id: string; config: IChartConfig }) {
  const colorConfig = Object.entries(config).filter(
    ([, styleConfig]) => styleConfig.theme || styleConfig.color
  );

  if (!colorConfig.length) {
    return null;
  }

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: Object.entries(THEMES)
          .map(
            ([theme, prefix]) => `
              ${prefix} [data-chart=${id}] {
              ${colorConfig
                .map(([key, itemConfig]) => {
                  const color =
                    itemConfig.theme?.[
                    theme as keyof typeof itemConfig.theme
                    ] || itemConfig.color;
                  return color ? `  --color-${key}: ${color};` : null;
                })
                .join('\n')}
              }
              `
          )
          .join('\n'),
      }}
    />
  );
}

/**
 * -----------------------------------------------------------------------------
 * Main component to apply the chart styles and the context.
 */
interface IChartContainerProps
  extends ComponentProps<'div'> {
  config: IChartConfig;
  children: ComponentProps<
    typeof RechartsPrimitive.ResponsiveContainer
  >['children'];
}

export function ChartContainer({
  children,
  className,
  config,
  id,
  ...props
}: IChartContainerProps) {
  const uniqueId = useId();
  const chartId = `chart-${id || uniqueId.replace(/:/g, '')}`;

  const memoizedConfig = useMemo(() => ({ config }), [config]);

  return (
    <ChartContext.Provider value={memoizedConfig}>
      <div
        className={cn(
          `[&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground
          [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50
          [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts
          -polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-
          background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-
          cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke
          -border [&_.recharts-layer]:outline-hidden [&_.recharts-sector]:outline
          -hidden [&_.recharts-surface]:outline-hidden flex aspect-video justify-c
          enter text-xs [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_
          .recharts-sector[stroke='#fff']]:stroke-transparent`,
          className
        )}
        data-chart={chartId}
        data-slot="chart"
        {...props}
      >
        <ChartStyle config={config} id={chartId} />
        <RechartsPrimitive.ResponsiveContainer>
          {children}
        </RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
}
